import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useLocation, Navigate } from 'react-router-dom'
import { loadStripe } from '@stripe/stripe-js'
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js'
import Header from '../components/Header'
import { useI18n } from '../i18n/useI18n'
import { api } from '../services/api'

/**
 * #1764 クレジットカードのチャージ (Stripe)。
 *
 * これまで「クレジットカード (デモ)」は `/charge/confirm` のモック (カードに請求しない、
 * Web3 実験の TestJPYC mint 付き) に進んでいた。本番と demo を同じにするため、
 * backend に既にあった `mode='stripe'` (Stripe PaymentIntent、card のみ) をここから使う。
 *
 * flow:
 *   1. `/create-intent` mode=stripe で PI 発行 (client_secret 取得)
 *   2. Stripe Elements の PaymentElement (card) を描画
 *   3. `stripe.confirmPayment` (redirect: if_required)。3D セキュアが要るカードだけ
 *      発行会社のページへ飛び、`/charge/return?charge_id=...` に戻る
 *   4. リダイレクト無しで succeeded なら、そのまま `/charge/return` へ遷移して
 *      status の polling (Stripe との照合で残高反映) → /charge/success
 *
 * モックの経路は Web3 mode の「デジタル通貨 (TestJPYC)」としてだけ残す (ChargeMethodPicker)。
 */

const stripePromise = loadStripe(
  import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || '',
)

type CreateIntentResponse = {
  charge_id: string
  amount_jpy: number
  fee_jpy: number
  total_jpy: number
  payment_intent_client_secret?: string | null
  payment_intent_id?: string | null
}

function CardForm({
  chargeId,
  clientSecret,
  totalJpy,
  amountJpy,
  feeJpy,
}: {
  chargeId: string
  clientSecret: string
  totalJpy: number
  amountJpy: number
  feeJpy: number
}) {
  const stripe = useStripe()
  const elements = useElements()
  const navigate = useNavigate()
  const { t } = useI18n()
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!stripe || !elements) return
    setSubmitting(true)
    setError(null)

    const returnUrl = `${window.location.origin}/charge/return?charge_id=${encodeURIComponent(chargeId)}`
    const { error: stripeError, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: { return_url: returnUrl },
      // 3D セキュアが不要なカードはリダイレクトせずにここで完了する
      redirect: 'if_required',
    })

    if (stripeError) {
      setError(stripeError.message || t('chargeLocal.errorConfirm'))
      setSubmitting(false)
      return
    }
    // リダイレクト無しで完了 (succeeded / processing)。ChargeReturn が Stripe の
    // 状態を確認して intent の polling → 残高反映 → success に進める。
    const status = paymentIntent?.status ?? 'succeeded'
    navigate(
      `/charge/return?charge_id=${encodeURIComponent(chargeId)}` +
        `&payment_intent_client_secret=${encodeURIComponent(clientSecret)}` +
        `&redirect_status=${encodeURIComponent(status === 'succeeded' ? 'succeeded' : status)}`,
      { replace: true },
    )
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4 p-4">
      <PaymentElement options={{ layout: 'tabs' }} />

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3">
          <p className="font-body text-sm text-red-600">{error}</p>
        </div>
      )}

      <div className="rounded-lg bg-gray-50 p-3 text-xs text-text-secondary">
        <div className="flex justify-between">
          <span>{t('chargeLocal.chargeAmount')}</span>
          <span>&yen; {amountJpy.toLocaleString()}</span>
        </div>
        <div className="mt-1 flex justify-between">
          <span>{t('chargeLocal.fee')}</span>
          <span>&yen; {feeJpy.toLocaleString()}</span>
        </div>
        <div className="mt-1 flex justify-between border-t border-gray-200 pt-1 font-semibold text-text-primary">
          <span>{t('chargeLocal.total')}</span>
          <span>&yen; {totalJpy.toLocaleString()}</span>
        </div>
      </div>

      <button
        type="submit"
        disabled={!stripe || submitting}
        className="tap-effect h-14 w-full rounded-xl bg-yokoso-red font-display text-base font-semibold text-white shadow-button disabled:opacity-50"
      >
        {submitting
          ? t('chargeLocal.submitting')
          : t('chargeLocal.payCta', { amount: totalJpy.toLocaleString() })}
      </button>
    </form>
  )
}

export default function ChargeCard() {
  const navigate = useNavigate()
  const location = useLocation()
  const { t } = useI18n()

  const state = (location.state ?? {}) as { amount?: number }
  const [loading, setLoading] = useState(true)
  const [intent, setIntent] = useState<CreateIntentResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  // React StrictMode の double-fire で PaymentIntent を 2 つ作らない (ChargeLocal と同じ)
  const creatingRef = useRef(false)

  useEffect(() => {
    if (!state.amount) return
    if (creatingRef.current) return
    creatingRef.current = true
    ;(async () => {
      try {
        const res = await api.createChargeIntent(state.amount!, 'stripe')
        if (!res || !res.payment_intent_client_secret) {
          throw new Error(t('chargeLocal.errorNoClientSecret'))
        }
        setIntent(res)
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e))
      } finally {
        setLoading(false)
      }
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.amount])

  const elementsOptions = useMemo(() => {
    if (!intent?.payment_intent_client_secret) return null
    return {
      clientSecret: intent.payment_intent_client_secret,
      appearance: {
        theme: 'stripe' as const,
        variables: {
          colorPrimary: '#C0392B',
          borderRadius: '12px',
          fontFamily: '"Inter", "Noto Sans JP", system-ui, sans-serif',
        },
      },
    }
  }, [intent?.payment_intent_client_secret])

  if (!state.amount) {
    return <Navigate to="/charge" replace />
  }

  return (
    <div className="flex min-h-screen flex-col bg-warm-white safe-top safe-bottom">
      <Header title={t('chargeCard.title')} onBack={() => navigate(-1)} />

      <div className="mx-4 mt-3 rounded-lg border border-yellow-200 bg-yellow-50 p-3">
        <p className="font-body text-xs text-yellow-800 leading-5">
          {t('chargeCard.note')}
        </p>
      </div>

      {loading && (
        <div className="p-6 text-center font-body text-sm text-text-secondary">
          {t('chargeLocal.preparing')}
        </div>
      )}

      {error && (
        <div className="mx-4 mt-4 rounded-lg border border-red-200 bg-red-50 p-3">
          <p className="font-body text-sm text-red-600">
            {t('chargeLocal.errorLoad')}
          </p>
          <p className="font-body text-xs text-red-500 mt-1 break-all">{error}</p>
        </div>
      )}

      {intent && elementsOptions && intent.payment_intent_client_secret && (
        <div className="flex-1">
          <Elements stripe={stripePromise} options={elementsOptions}>
            <CardForm
              chargeId={intent.charge_id}
              clientSecret={intent.payment_intent_client_secret}
              totalJpy={intent.total_jpy}
              amountJpy={intent.amount_jpy}
              feeJpy={intent.fee_jpy}
            />
          </Elements>
        </div>
      )}
    </div>
  )
}
